import * as RadixAvatar from '@radix-ui/react-avatar';

import { cn } from '../../lib/utils';

const sizeMap = {
  xs: 'h-5 w-5 text-[9px]',
  sm: 'h-7 w-7 text-[11px]',
  md: 'h-9 w-9 text-xs',
  lg: 'h-11 w-11 text-sm',
  xl: 'h-14 w-14 text-base',
} as const;

// 8 deterministic palette colours
const avatarColors = [
  { bg: '#4f46e5', text: '#ffffff' },
  { bg: '#7c3aed', text: '#ffffff' },
  { bg: '#2563eb', text: '#ffffff' },
  { bg: '#059669', text: '#ffffff' },
  { bg: '#d97706', text: '#ffffff' },
  { bg: '#dc2626', text: '#ffffff' },
  { bg: '#0891b2', text: '#ffffff' },
  { bg: '#be185d', text: '#ffffff' },
];

const fallbackColor = avatarColors[0] as { bg: string; text: string };

function getAvatarColor(name: string): { bg: string; text: string } {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return avatarColors[Math.abs(hash) % avatarColors.length] ?? fallbackColor;
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? '').join('') || 'U';
}

// ── Avatar ────────────────────────────────────────────────────

interface AvatarProps {
  src?: string;
  name?: string;
  size?: keyof typeof sizeMap;
  shape?: 'circle' | 'square';
  className?: string;
}

export function Avatar({ src, name = '', size = 'md', shape = 'circle', className }: AvatarProps) {
  const color = getAvatarColor(name || 'U');
  return (
    <RadixAvatar.Root
      className={cn(
        'relative inline-flex shrink-0 select-none overflow-hidden',
        sizeMap[size],
        shape === 'circle' ? 'rounded-full' : 'rounded-lg',
        className,
      )}
    >
      {src && (
        <RadixAvatar.Image
          src={src}
          alt={name}
          className="h-full w-full object-cover"
        />
      )}
      <RadixAvatar.Fallback
        style={{ backgroundColor: color.bg, color: color.text }}
        className="flex h-full w-full items-center justify-center font-semibold"
      >
        {getInitials(name)}
      </RadixAvatar.Fallback>
    </RadixAvatar.Root>
  );
}

// ── AvatarGroup ───────────────────────────────────────────────

interface AvatarGroupProps {
  avatars: Array<{ src?: string; name?: string }>;
  max?: number;
  size?: keyof typeof sizeMap;
}

export function AvatarGroup({ avatars, max = 4, size = 'sm' }: AvatarGroupProps) {
  const visible = avatars.slice(0, max);
  const overflow = avatars.length - max;

  return (
    <div className="flex items-center">
      {visible.map((av, i) => (
        <div
          key={i}
          className={cn('-ml-2 first:ml-0 ring-2 ring-surface-card rounded-full')}
        >
          <Avatar {...av} size={size} />
        </div>
      ))}
      {overflow > 0 && (
        <div
          className={cn(
            '-ml-2 flex items-center justify-center rounded-full',
            'ring-2 ring-surface-card bg-bg-emphasis text-text-secondary font-medium',
            sizeMap[size],
          )}
        >
          <span className="text-[10px]">+{overflow}</span>
        </div>
      )}
    </div>
  );
}
